import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, CircularProgress, Alert, Button,
  TextField, MenuItem, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Collapse, Switch, FormControlLabel,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { queryLogs } from '../services/api';

const LEVELS = ['Verbose', 'Debug', 'Information', 'Warning', 'Error', 'Fatal'];

const LEVEL_COLOR = {
  Verbose:     'default',
  Debug:       'default',
  Information: 'info',
  Warning:     'warning',
  Error:       'error',
  Fatal:       'error',
};

function fmtTs(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString();
}

function LogRow({ entry }) {
  const [open, setOpen] = useState(false);
  const hasDetail = entry.exception || (entry.properties && Object.keys(entry.properties).length > 0);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 32 }}>
          {hasDetail && (
            <IconButton size="small" onClick={() => setOpen(o => !o)}>
              {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', width: 170 }}>
          <Typography variant="caption">{fmtTs(entry.timestamp)}</Typography>
        </TableCell>
        <TableCell sx={{ width: 110 }}>
          <Chip size="small" color={LEVEL_COLOR[entry.level] || 'default'} label={entry.level} />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {entry.message}
          </Typography>
        </TableCell>
      </TableRow>
      {hasDetail && (
        <TableRow>
          <TableCell colSpan={4} sx={{ py: 0, borderBottom: open ? undefined : 'unset' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                {entry.exception && (
                  <>
                    <Typography variant="caption" color="text.secondary">Exception</Typography>
                    <Box component="pre" sx={{
                      m: 0, mt: 0.5, mb: 2, p: 1, bgcolor: 'grey.900', color: 'grey.100',
                      fontSize: 12, overflow: 'auto', borderRadius: 1, maxHeight: 320,
                    }}>
                      {entry.exception}
                    </Box>
                  </>
                )}
                {entry.properties && Object.keys(entry.properties).length > 0 && (
                  <>
                    <Typography variant="caption" color="text.secondary">Properties</Typography>
                    <Table size="small" sx={{ mt: 0.5 }}>
                      <TableBody>
                        {Object.entries(entry.properties).map(([k, v]) => (
                          <TableRow key={k}>
                            <TableCell sx={{ width: 220, fontFamily: 'monospace', fontSize: 12 }}>{k}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{v}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function LogsPage() {
  const [level, setLevel] = useState('Warning,Error,Fatal');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [take, setTake] = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { take };
      if (level) params.level = level;
      if (q) params.q = q;
      if (from) params.from = new Date(from).toISOString();
      if (to)   params.to   = new Date(to).toISOString();
      const { data } = await queryLogs(params);
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [level, q, from, to, take]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(fetchLogs, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, fetchLogs]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Application Logs</Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <FormControlLabel
            control={<Switch checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />}
            label="Auto-refresh (5s)"
          />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchLogs} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            select size="small" label="Level" value={level} sx={{ minWidth: 220 }}
            onChange={(e) => setLevel(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="Warning,Error,Fatal">Warning + Error + Fatal</MenuItem>
            <MenuItem value="Error,Fatal">Error + Fatal</MenuItem>
            {LEVELS.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
          </TextField>
          <TextField
            size="small" label="Search (message / exception)" value={q} sx={{ minWidth: 280 }}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchLogs(); }}
          />
          <TextField
            size="small" label="From (UTC)" type="datetime-local" value={from}
            InputLabelProps={{ shrink: true }} onChange={(e) => setFrom(e.target.value)}
          />
          <TextField
            size="small" label="To (UTC)" type="datetime-local" value={to}
            InputLabelProps={{ shrink: true }} onChange={(e) => setTo(e.target.value)}
          />
          <TextField
            select size="small" label="Limit" value={take} sx={{ width: 110 }}
            onChange={(e) => setTake(Number(e.target.value))}
          >
            {[50, 100, 200, 500, 1000].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Loading…' : `${count} entries`}
          </Typography>
          {loading && <CircularProgress size={16} />}
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Time</TableCell>
              <TableCell>Level</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No log entries match these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {items.map((entry) => <LogRow key={entry.id} entry={entry} />)}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
